'use client'
import React, { useEffect, useMemo, useState } from 'react';
import { VStack, HStack, Box, Text, Button, Spacer } from '@chakra-ui/react';
import { useToast } from '@mybridge/ui';
import { useMutation } from '@tanstack/react-query';
import { GoogleMeetSuggestion } from '@mybridge/icons/GoogleMeetSuggestion';
import { GoogleMeetIcon } from '@mybridge/icons/GoogleMeetIcon';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
} from '@mybridge/ui';
import { useDispatch, useSelector } from 'react-redux';
import API from 'v4/store/common/api';
import {getMsTeamsIntegrationURL} from 'v4/lib/api/google-integration';
import { useRouter } from 'next/router';
import { getGoogleIntegrationCallback, getMsTeamsIntegrationCallback } from 'v4/store/slices/settingsSlice';
import { MicrosoftTeamsSuggestion } from '@mybridge/icons/MicrosoftTeamsSuggestion';
import { MicroSoftTeamsIcon } from '@mybridge/icons/MicroSoftTeamsIcon';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';




const MicrosoftMeetingIntegration = ({ calendars, setCalendars }) => {
  const { t } = useTranslator();
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const { confrenceList } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState({
    isOpen: false,
  });

  // Handle calendar removal
  const handleRemoveCalendar = async (selectedAccount) => {
    try {
      const response = await API({
        url: `api/v1/conference/${selectedAccount?.id}`,
        method: 'DELETE',
      });

      if (response?.message === 'appointment deleted successfully') {
        toast({
          title: t('settingsIntegration.removeCalendarSuccess') || 'Calendar removed successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setCalendars((prevCalendars) =>
          prevCalendars.map((calendar) =>
            calendar.id === selectedAccount?.id
              ? { ...calendar, isConnected: false }
              : calendar
          )
        );
      } else {
        throw new Error('Failed to remove');
      }
    } catch (error) {
      toast({
        title: t('settingsIntegration.errorRemoving') || 'Error removing calendar.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsConfirmRemoveModalOpen({ isOpen: false });
    }
  };



  useEffect(() => {
    if (confrenceList?.length > 0) {
      // Log the initial state of confrenceList and calendars to verify data structure


  
      const updatedSelected = confrenceList
        .map((cal) => {  
          // Find the teams calendar from the calendars list
          const calendar = calendars?.find((calendar) => {
            // Ensure we are matching only teams calendars
            const isteamsProvider = cal?.provider?.toLowerCase() === 'ms_teams';
            const isteamsKey = calendar?.key?.toLowerCase().includes('ms_teams');
  
            return isteamsProvider && isteamsKey; // Both must be true
          });
  
          // Log matched calendar to verify correct mapping

  
          if (calendar) {
            return { ...calendar, email: cal?.calendar_id, id: cal?.id };
          }
          return null;
        })
        .filter(Boolean); // Filter out any null values
  
      // Log the final selected calendars for verification

  
      // Update the state with the selected calendars
      setSelectedCalendars(updatedSelected);
    }
  }, [confrenceList, calendars]);


  // Find Google Meet calendar from the list
  const googleMeetCalendar = useMemo(
    () => calendars?.find((calendar) => calendar?.key === 'ms_teams'),
    [calendars]
  );

  // Handle Google integration callback
  useEffect(() => {
    if (typeof window !== 'undefined' && router?.query?.code) { // Ensure code runs only on client-side
      const payload = {
        state: router?.query?.state,
        scope: router?.query?.scope,
        code: router?.query?.code,
        prompt: router?.query?.prompt,
        authuser: router?.query?.authuser,
        hd: router?.query?.hd,
      };
      dispatch(getMsTeamsIntegrationCallback(payload)).then((res) => {
        toast({
          title: 'Success',
          description: res?.payload?.data,
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
        router.push('/settings/integration?status=success');
      });
    }
  }, [router, dispatch, toast]);
  const { mutate, isLoading, error } = useMutation(getMsTeamsIntegrationURL, {
    onSuccess: (data) => {
      if (data?.error) {
        alert(data?.error);
      } else {
        const { url = null } = data;
        if (url) {
          router?.push(url); // Use router push to redirect to the URL
        }
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return (
    <>
      {googleMeetCalendar?.isConnected ? (
        // <GoogleMeetConnected
        //   setIsConfirmRemoveModalOpen={setIsConfirmRemoveModalOpen}
        //   selectedCalendar={selectedCalendars[0]}
        // />


        <VStack
        spacing={6}
        align="start"
        p={6}
        mt={6}
        w="100%"
        maxW="100%"
        borderRadius="20px"
        boxShadow="0px 0px 12px 0px #00000033"
        backgroundColor="white"
        fontFamily="Inter"
      >
        <VStack
         w="100%"
         p={4}
         alignItems="start"
         borderRadius="4px"
         border="1px solid #D9D9D9"
         backgroundColor="#FFFFFF"
         spacing={4}
        >
          <Text fontSize="18px" fontWeight="400" color="#747474">
          {t('settingsIntegration.teamsConnected') || "Congratulations! You can already schedule with Microsoft teams video conferencing because your Microsoft teams account is connected with myBridge."}
          </Text>
          <HStack w="100%" alignItems="center">
            <MicroSoftTeamsIcon width="80" height="80" />
            <VStack  w="80%" gap={0} h={46} alignItems="flex-start">
              <Text fontSize="12px" fontWeight="400" color="#37ABE7">
                {t('settingsIntegration.connectedBy') || "CONNECTED BY"}
              </Text>
              <Text fontSize="22px" fontWeight="500" color="#5B5B5B">
                {/* {selectedCalendars?.[0]?.email || 'No email found'} */}
                {selectedCalendars?.[0]?.email.length>30? selectedCalendars?.[0]?.email.slice(0,30)+'...':selectedCalendars?.[0]?.email  || 'No email found'}

              </Text>
              <Spacer />
            </VStack>
            <Button
              borderRadius="5px"
              fontWeight={600}
              fontSize="14px"
              color="#3D5A80"
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmRemoveModalOpen({ isOpen: true })}
            >
              {t('settingsIntegration.disconnect') || "Disconnect"}
            </Button>
          </HStack>
        </VStack>
    
        <VStack
          w="100%"
          p={4}
          maxW="100%"
          alignItems="start"
          borderRadius="4px"
          border="1px solid #D9D9D9"
          backgroundColor="#FFFFFF"
          spacing={4}
        >
          <Text fontSize="18px" color="#5B5B5B">
            {t('settingsIntegration.teamsFeatures') || "With your connected Microsoft teams you can"}:
          </Text>
          <HStack w="100%" p="14px" justifyContent="space-between">
            <MicrosoftTeamsSuggestion />
            <Box w="50%">
              <Text fontSize="14px" fontWeight="500" color="#5B5B5B">
                {t('settingsIntegration.setDefaultLocation') || "Set a default location for event types."}
              </Text>
              <Text fontSize="12px" color="#747474" mt={2}>
                {t('settingsIntegration.teamsDefault') || "Select Microsoft teams as a default location for any of your event types to have meeting details automatically added to calendar events."}
              </Text>
            </Box>
          </HStack>
        </VStack>
      </VStack>
      ) : (
         <VStack
      spacing={4}
      align="start"
      p="16px 0 30px 16px"
      mt={6}
      maxW="100%"  
      w="100%"
      borderRadius="4px"
      boxShadow="0px 0px 12px 0px #00000033"
      backgroundColor="white"
      fontFamily="Inter"
    
    >
      <Text fontSize="18px" fontWeight="400" color="#5B5B5B">
        {t('settingsIntegration.teamsConferencing') || "Microsoft teams conferencing"}
      </Text>
      <HStack alignItems="flex-start">
        <VStack spacing={4} pr="16px">
          <Text fontSize="14px" color="#747474">
            {t('settingsIntegration.addTeams') || "Add Microsoft Teams as a location option for your meeting and events with myBridge's Microsoft Teams Conferencing integration."}
          </Text>
          <Text fontSize="12px" color="#747474">
            {t('settingsIntegration.integrateTeams') || "Integrate Microsoft Teams with myBridge to add Teams video conferencing as a location option for events you schedule with myBridge. This way, you don’t have to edit your calendar app’s invitations to add the Teams conferencing information (which is a lot of manual work and confuses invitees)."}
          </Text>
          <HStack spacing={4} w="100%">
            <VStack alignItems="flex-start">
              <MicrosoftTeamsSuggestion />
              <Text my={2} fontSize="10px" color="#747474">
                {t('settingsIntegration.setTeamsLocation') || "Set Microsoft teams conferencing as the location for an event type to add conferencing information to the calendar invitation."}
              </Text>
              <Button
              borderRadius="30px"
              fontWeight={600}
              fontSize="16px"
              color="#FFFFFF"
              _hover={{bg:"#3D5A80"}}
              p="10px 30px"
              variant="outline"
              size="sm"
              bg="#3D5A80"
              onClick={() => mutate()}
            >
             {t('settingsIntegration.integrateNow') || "Integrate now"}
            </Button>
            </VStack>
          </HStack>
        </VStack>
      
      </HStack>

    </VStack>
      )}

      <Modal
        isOpen={isConfirmRemoveModalOpen?.isOpen}
        onClose={() => setIsConfirmRemoveModalOpen({ isOpen: false })}
        isCentered
      >
        <ModalOverlay />
        <ModalContent w={350} h={107}>
          <ModalCloseButton />
          <ModalBody>
            <Stack mb={2}>
              <Text fontSize={16} color="#5B5B5B">
                {t('settingsIntegration.disconnect') || "Disconnect"} Microsoft Teams?
              </Text>
            </Stack>
            <ModalFooter pr={0} pt={4}>
              <Button
                borderRadius={'full'}
                fontSize={16}
                variant="ghost"
                onClick={() => setIsConfirmRemoveModalOpen({ isOpen: false })}
              >
                {t('myCalendar.dismiss') || "Dismiss"}
              </Button>
              <Button
                borderRadius={'full'}
                variant={'primary'}
                fontSize={15}
                onClick={() => handleRemoveCalendar(selectedCalendars?.[0])}
                // backgroundColor="#E4E6EB"
              >
                {t('myCalendar.confirm') || "Confirm"}
              </Button>
            </ModalFooter>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};


export default MicrosoftMeetingIntegration;
